import { faCircleEllipsis } from "@fortawesome/pro-regular-svg-icons/faCircleEllipsis";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Divider, Menu, MenuItem } from "@mui/material";
import { fetchKnowledgeQuickAccess } from "app/features/menu/menuSlice";
import { store } from "app/store";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { FeedBlock, FeedItemDto, MemberType, PublicationType } from "generated/CoreApi_v1";
import HtmlUtils from "lib/HtmlUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { PublicationFeed } from "views/knowledge/components/card";
import { AddToListDrawer } from "views/knowledge/components/card/actions/AddToListDrawer";
import { BlockDialog } from "views/knowledge/components/card/actions/BlockDialog";
import { PublicationLogDialog } from "views/knowledge/components/card/actions/PublicationLogDialog";
import { PublicationRelationshipDialog } from "views/knowledge/components/card/actions/PublicationRelationshipDialog";
import { RecommendPublicationDialog } from "views/knowledge/components/card/actions/RecommendPublicationDialog";
import { ReportErrorOrAbuseDialog } from "views/knowledge/components/card/actions/ReportErrorOrAbuseDialog";
import { SharePublicationDialog } from "views/knowledge/components/card/actions/SharePublicationDialog";
import { DeletePublicationFactory } from "views/knowledge/components/card/behaviors/common/DeletePublicationFactory";
import { EditPublicationFactory } from "views/knowledge/components/card/behaviors/common/EditPublicationFactory";
import { classifyFactory, publicationFactory } from "views/knowledge/feed/FeedHelpers";

interface IProps {
    publication: FeedItemDto;
    onChanged: (data: Partial<PublicationFeed>) => void;
}

///
const MenuBaseOptions: React.FC<IProps> = (props) => {
    const navigate = useNavigate();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();

    const { publication, onChanged } = props;

    const {
        publicationId,
        publicationType,
        masterPublication,
        title,
        isDraft,
        readOnly,
        interaction: {
            allowDelete,
            allowBlock,
            allowEdit,
            allowClassify,
            allowViewHistory,
            allowCreateQuestion,
            allowRelatedContent,
            followingPublication
        },
        interaction,
        isPrivate,
    } = publication;

    const [anchor, setAnchor] = React.useState<Element | null>(null);
    const [dialog, setDialog] = React.useState<any>(null);

    type MenuOptions =
        | "openList"
        | "follow"
        | "unfollow"
        | "addToList"
        | "recommendToUsers"
        | "shareChannel"
        | "copyLink"
        | "edit"
        | "classify"
        | "relatedContent"
        | "createTask"
        | "createQuestion"
        | "history"
        | "abuseOrError"
        | "block"
        | "delete"
        ;
    type MenuItem = { key: MenuOptions, label: string; disabled?: boolean; };

    const closeDialog = () => {
        setDialog(null);
    };

    const handleCloseClassify = (changed: boolean) => {
        if (changed) {
            onChanged({
                interaction: { ...interaction, reclassified: changed }
            });
        }
        setDialog(null);
    };

    const groupOptions: Array<Array<MenuItem>> = [];

    const groupItems1: MenuItem[] = [];
    groupItems1.push({ key: "openList", label: t("Feed.Card.Options.List.OpenListAction") });
    if (groupItems1.length > 0) groupOptions.push(groupItems1);

    const groupItems2: MenuItem[] = [];
    if (!isPrivate && !followingPublication) groupItems2.push({ key: "follow", label: t("Feed.Card.Options.List.FollowAction"), disabled: readOnly });
    if (!isPrivate && followingPublication) groupItems2.push({ key: "unfollow", label: t("Feed.Card.Options.List.UnfollowAction"), disabled: readOnly });
    groupItems2.push({ key: "addToList", label: t("Feed.Card.Options.List.AddToListAction"), disabled: readOnly });
    if (!isPrivate) groupItems2.push({ key: "recommendToUsers", label: t("Feed.Card.Options.List.RecommendToUsersAction"), disabled: readOnly });
    if (!isPrivate) groupItems2.push({ key: "shareChannel", label: t("Feed.Card.Options.List.ShareChannelAction"), disabled: readOnly });
    if (!isPrivate) groupItems2.push({ key: "copyLink", label: t("Feed.Card.Options.CopyLinkAction"), disabled: readOnly });
    if (groupItems2.length > 0) groupOptions.push(groupItems2);

    const groupItems3: MenuItem[] = [];
    if (allowEdit) groupItems3.push({ key: "edit", label: t("Feed.Card.Options.List.Edit") });
    if (allowClassify) groupItems3.push({ key: "classify", label: t("Feed.Card.Options.ClassifyAction") });
    if (allowRelatedContent) groupItems3.push({ key: "relatedContent", label: t("Feed.Card.Options.RelatedContentAction") });
    if (groupItems3.length > 0) groupOptions.push(groupItems3);

    const groupItems4: MenuItem[] = [];
    if (allowCreateQuestion) groupItems4.push({ key: "createQuestion", label: t("Feed.Card.Options.CreateQuestionAction"), disabled: readOnly });
    if (groupItems4.length > 0) groupOptions.push(groupItems4);

    const groupItems5: MenuItem[] = [];
    if (allowViewHistory) groupItems5.push({ key: "history", label: t("Feed.Card.Options.HistoryAction") });
    if (groupItems5.length > 0) groupOptions.push(groupItems5);

    const groupItems6: MenuItem[] = [];
    if (!isPrivate) groupItems6.push({ key: "abuseOrError", label: t("Feed.Card.Options.AbuseOrReportAction"), disabled: readOnly });
    if (allowBlock) groupItems6.push({ key: "block", label: t("Feed.Card.Options.BlockAction"), disabled: readOnly });
    if (allowDelete) groupItems6.push({ key: "delete", label: t("Feed.Card.Options.RemoveAction") });
    if (groupItems6.length > 0) groupOptions.push(groupItems6);

    // Opções do menu
    const menuItems = useMemo(() => {
        var arr: React.ReactNode[] = [];

        groupOptions.forEach((g, gindex) => {

            g.forEach((v) => {
                arr.push(<MenuItem
                    key={v.key}
                    disabled={v.disabled}
                    sx={(theme) => ({ color: v.key == "delete" ? theme.palette.error.main : undefined })}
                    onClick={
                        () => {
                            handleChooseMenuItem(v.key);
                            setAnchor(null);
                        }
                    }
                >
                    {v.label}
                </MenuItem>);
            });

            if (gindex < groupOptions.length - 1)
                arr.push(<Divider key={gindex} />);
        });

        return arr;
    }, [groupOptions]);

    ///
    const handleClosePublication = () => {
        setDialog(null);
    };

    ///
    const handleFollow = () => {
        api.v1.knowledgeSubcription.subscribePublication({ publicationId })
            .then((result) => {
                if (result) {
                    enqueueSnackbar(t("Publication.ListPlayback.FollowingMessage"), { variant: "success" });
                    store.dispatch(fetchKnowledgeQuickAccess({}));
                    onChanged({
                        interaction: { ...interaction, followingPublication: true },
                    });
                }
            })
            .catch((e) => { });
    };

    ///
    const handleUnfollow = () => {
        api.v1.knowledgeSubcription.unsubscribePublication({ publicationId })
            .then((result) => {
                if (result) {
                    enqueueSnackbar(t("Publication.ListPlayback.UnfollowingMessage"), { variant: "success" });
                    store.dispatch(fetchKnowledgeQuickAccess({}));
                    onChanged({
                        interaction: { ...interaction, followingPublication: false },
                    });
                }
            })
            .catch((e) => { });
    };

    ///
    const copyLink = () => {
        const host = window.location.protocol + "//" + window.location.host;
        const publicationUrl = `${host}${KnowledgePaths.ListPublicationInPlayback}/${publicationId}`;

        HtmlUtils.copyToClipboard(publicationUrl)
            .then(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkSuccessMessage"), { variant: "success" }))
            .catch(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkFailMessage"), { variant: "error" }));
    };

    ///
    const handleChooseMenuItem = (option: MenuOptions) => {
        setAnchor(null);
        switch (option) {

            case "openList":
                navigate(`${KnowledgePaths.ListPublicationViewer}/${publicationId}`);
                break;

            case "follow":
                handleFollow();
                break;

            case "unfollow":
                handleUnfollow();
                break;

            case "addToList":
                setDialog(
                    <AddToListDrawer
                        publicationId={publication.publicationId}
                        subtitle={title}
                        onChanged={(items) => onChanged({ interaction: { ...interaction, hasFavorited: items.filter(p => p.selected).length > 0 } })}
                        onClose={() => setDialog(null)}
                    />
                );
                break;

            case "recommendToUsers":
                setDialog(
                    <RecommendPublicationDialog
                        publicationId={publicationId}
                        title={title}
                        memberType={MemberType.User}
                        onClose={() => setDialog(null)}
                    />
                );
                break;

            case "shareChannel":
                setDialog(
                    <SharePublicationDialog
                        publicationId={publicationId}
                        title={title}
                        onClose={() => setDialog(null)}
                    />
                );
                break;

            case "copyLink":
                copyLink();
                break;

            case "edit":
                setDialog(<EditPublicationFactory
                    publicationId={publicationId}
                    publicationType={publicationType}
                    masterPublicationId={masterPublication?.publicationId}
                    masterTitle={title}
                    onClose={() => setDialog(null)}
                />);
                break;

            case "delete":
                setDialog(<DeletePublicationFactory
                    publicationId={publicationId}
                    title={title}
                    isDraft={isDraft}
                    publicationType={publicationType}
                    isThread={masterPublication != null}
                    onClose={
                        (deleted) => {
                            if (deleted) onChanged({ deletedOn: new Date() });
                            setDialog(null);
                        }
                    }
                />);
                break;

            case "relatedContent":
                setDialog(<PublicationRelationshipDialog
                    publicationId={publicationId}
                    title={title}
                    onClose={
                        (changed) => {
                            if (changed) onChanged({});
                            setDialog(null);
                        }
                    }
                />);
                break;

            case "history":
                setDialog(<PublicationLogDialog
                    publicationId={publicationId}
                    title={title}
                    onClose={closeDialog}
                />);
                break;

            case "classify":
                setDialog(classifyFactory(publicationId, publicationType, title, handleCloseClassify));
                break;

            case "block":
                setDialog(
                    <BlockDialog
                        onClose={
                            (blocked) => {
                                if (blocked) onChanged({ blocked: { blockedOn: new Date() } as FeedBlock });
                                setDialog(null);
                            }
                        }
                        publicationId={publicationId}
                        title={title}
                    />);
                break;

            case "abuseOrError":
                setDialog(
                    <ReportErrorOrAbuseDialog
                        onClose={() => setDialog(null)}
                        publicationId={publicationId}
                        title={title}
                    />
                );
                break;

            case "createQuestion":
                setDialog(publicationFactory(null, PublicationType.Question, handleClosePublication,
                    {
                        mentions: [{
                            id: publicationId,
                            ot: "pub",
                            value: title,
                            denotationChar: "P"
                        }]
                    }));
                break;
        }
    };

    return (
        <>
            {dialog}
            <MprcFeedTypography variant="action">
                <Box>
                    <Box
                        sx={(theme) => ({
                            '&:hover': {
                                cursor: "pointer"
                            },
                        })}
                        onClick={(e) => setAnchor(e.currentTarget)}
                    >
                        <FontAwesomeIcon icon={faCircleEllipsis} size="lg" />
                    </Box>
                    <Menu
                        anchorEl={anchor}
                        open={Boolean(anchor)}
                        onClose={() => setAnchor(null)}
                    >
                        {menuItems}
                    </Menu>
                </Box>
            </MprcFeedTypography>
        </>
    );
};


export const PlaybackOptions: React.FC<IProps> = (props) => {

    const { publication, onChanged } = props;

    return (
        <MenuBaseOptions
            publication={publication}
            onChanged={onChanged}
        />
    );
};
